import React, { useEffect, useState } from 'react';
import { useRouteError } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import {
  isChunkLoadError,
  isReloadInitiated,
  onChunkReloadCancelled,
  reloadOnceForChunkError,
  wasChunkReloadCancelled,
} from 'utils/chunkReload';

import { ChunkErrorFallback } from './ChunkErrorFallback';

// errorElement for the data router. createBrowserRouter catches errors thrown
// while rendering a route element — including a lazy chunk that failed to load
// on a stale deploy — at the route level, which would otherwise show React
// Router's default error UI instead of reaching AppErrorBoundary. lazyWithReload
// already recovers most chunk failures at the import; this is the backstop for
// any that reach render (and the friendly fallback for genuine route errors).
export const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const chunkError = isChunkLoadError(error);
  // Start in the recovering (render-nothing) state for chunk errors so the
  // fallback never flashes before a reload is decided.
  const [recovering, setRecovering] = useState(chunkError);

  useEffect(() => {
    if (chunkError) {
      // Reload once; if the one-shot guard was already spent (the error
      // survived a reload) and no reload is pending, show the fallback instead.
      if (
        !reloadOnceForChunkError(error, 'route_error') &&
        !isReloadInitiated()
      ) {
        setRecovering(false);
        datadogRum.addError(error, {
          feature: 'route_error_boundary',
          chunkReloadCancelled: wasChunkReloadCancelled(),
        });
        return;
      }
      return onChunkReloadCancelled(() => {
        setRecovering(false);
        datadogRum.addError(error, {
          feature: 'route_error_boundary',
          chunkReloadCancelled: true,
        });
      });
    }
    datadogRum.addError(error, { feature: 'route_error_boundary' });
    return undefined;
  }, [chunkError, error]);

  if (recovering) {
    return null;
  }
  return <ChunkErrorFallback onReload={() => window.location.reload()} />;
};
